import styles from './NotAvailable.module.scss';
import React, { useMemo } from 'react';
import { Button, Link } from '@wienenergiegithub/ui-next';
import classNames from 'classnames';
import AvailabilityAnswerRAG from '../../../Availability/AvailabilityComponents/AvailabilityAnswers/AvailabilityAnswerRAG/AvailabilityAnswerRAG';
import { useLocalStorage } from '@components/hooks';
import { Skeleton } from '@wienenergiegithub/ui-next';
import PropTypes from 'prop-types';

// Renders the "not available" Fernwaerme state for heat and general product recommenders
const NotAvailable = ({
  address,
  fernwaermeCase,
  heatResponses,
  onNewCheck = null,
  type = 'heat',
}) => {
  // Get matching heat response from local storage
  const [heatStorage, setHeatStorage] = useLocalStorage('heat-info');
  const heatResponseMatch = heatResponses?.find(
    e => e.option === (fernwaermeCase || heatStorage?.fernwaermeCase),
  );

  // Determine corresponding response from template settings for determined fernwaermeCase
  if (!heatResponseMatch && type === 'heat') {
    setHeatStorage({
      heat: {},
      fernwaermeCase: 'heatError',
      lastRequestParams: '',
    });
    return;
  }

  const { link, option, secondLink, text, title } = heatResponseMatch || {};

  const isFirstButtonDefined = link?.url && link?.title;
  const areBothButtonsDefined =
    isFirstButtonDefined && secondLink?.url && secondLink?.title;

  const iconColor = useMemo(
    () =>
      ['lokaleWaermeIndividuell', 'lokaleWaermeGemeinsam'].includes(option)
        ? 'orange'
        : 'green',
    [option],
  );

  return (
    <div
      className={classNames(styles.container, styles[type])}
      data-purpose="fernwaerme-not-available"
    >
      {!heatResponseMatch && (
        <div className={styles.skeleton}>
          <Skeleton count={4} height="5.78rem" />
        </div>
      )}
      <AvailabilityAnswerRAG
        address={address}
        content={text}
        heading={title}
        icon="circle-info"
        iconColor={iconColor}
      >
        <div className={styles.buttonContainer}>
          {isFirstButtonDefined && (
            <Button
              className={styles.notAvailableButton}
              id="not-available-fernwaerme-more"
              render={
                <Link
                  target={type === 'heat' ? link.target : '_blank'}
                  to={link.url}
                />
              }
              size="small"
              theme="primary"
            >
              {link.title}
            </Button>
          )}
          {areBothButtonsDefined && (
            <Button
              className={styles.notAvailableButton}
              id="not-available-fernwaerme-more-second"
              render={
                <Link
                  target={type === 'heat' ? secondLink.target : '_blank'}
                  to={secondLink.url}
                />
              }
              size="small"
              theme="primary"
            >
              {secondLink.title}
            </Button>
          )}
          {type === 'heat' && !!onNewCheck && (
            <Button
              className={styles.newCheckButton}
              id="not-available-fernwaerme-weitere-addresse-pruefen"
              onClick={onNewCheck}
              theme="secondary"
              variant="link"
            >
              Weitere Adresse prüfen
            </Button>
          )}
        </div>
      </AvailabilityAnswerRAG>
    </div>
  );
};

NotAvailable.propTypes = {
  address: PropTypes.string,
  fernwaermeCase: PropTypes.string,
  // "Raus Aus Gas" responses from template settings
  heatResponses: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.shape({
        url: PropTypes.string,
        title: PropTypes.string,
      }).isRequired,
      option: PropTypes.string,
      secondLink: PropTypes.shape({
        url: PropTypes.string,
        title: PropTypes.string,
        target: PropTypes.string,
      }),
      text: PropTypes.string,
      title: PropTypes.string,
    }),
  ).isRequired,
  onNewCheck: PropTypes.func,
  type: PropTypes.oneOf(['heat', 'general']),
};

export default NotAvailable;
